import React, { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, Tooltip, IconButton } from '@material-ui/core'

import { companyTypes, inviteStatus as inviteStatusType } from '@/consts/types'
import { useSortData, usePagination } from '@/hooks/index'
import {
  getCompaniesRequest,
  sendInviteRequest,
  logIsAsCompanyRequest,
} from '@/store/modules/admin/companies/reducer'

import { Add, Mail, Edit, Signout } from '@/components/Icons'
import {
  TablePagination,
  Content,
  Table,
  confirmationDialog,
} from '@/components'

import ComboboxUserStatus from '@/components/Combobox/UserStatus'
import ComboboxInviteStatus from '@/components/Combobox/InviteStatus'
import ComboboxCompanyTypes from '@/components/Combobox/CompanyTypes'
import { inviteStatus as inviteStatusTypes } from '@/consts/types'

import FormEdit from './FormEdit'
import Filter from './Filter'

import { StyledButton, LoginButton, Paper } from './styles'
import { Status } from '@/components/Status'
import formatter from '@/services/formatter'
import { unmask } from '@/services/masks'

import { BatchInvitationsButton } from './BatchInvitationsButton'
import { UpdateWebCompaniesButton } from './UpdateWebCompaniesButton'

// --------------- 𝕄𝕖𝕥𝕒𝕕𝕒𝕥𝕒 ---------------

const INITIAL_SORT = 'wee_tipo'

// --------------- 𝕄𝕒𝕚𝕟 ---------------

function AdminCompaniesPage() {
  const dispatch = useDispatch()

  const loading = useSelector((state) => state.admin.companies.loading)

  const webCompanies = useSelector(
    (state) => state.admin.companies.webCompanies
  )

  const webCompaniesCount = useSelector(
    (state) => state.admin.companies.webCompaniesCount
  )

  const [companyType, setCompanyType] = useState('')
  const [status, setStatus] = useState('')
  const [inviteStatus, setinviteStatus] = useState(inviteStatusTypes.ALL)

  const [selected, setSelected] = useState(null)
  const [selectedFilters, setSelectedFilters] = useState([])

  const { currentSort, onSortChange } = useSortData({
    initialField: INITIAL_SORT,
  })

  const { onChangePage, onChangeRowsPerPage, reset, rowsPerPage, page } =
    usePagination()

  const fetchCompanies = useCallback(() => {
    const localFilters = selectedFilters.reduce((acc, item) => {
      acc[item.filterBy] = item.value
      return acc
    }, {})

    dispatch(
      getCompaniesRequest({
        page,
        perPage: rowsPerPage,
        order: currentSort.field,
        orderAsc: currentSort.order,
        type: companyType,
        status,
        inviteStatus,
        query: localFilters,
      })
    )
  }, [
    dispatch,
    rowsPerPage,
    companyType,
    page,
    status,
    inviteStatus,
    currentSort,
    selectedFilters,
  ])

  // ... handlers
  function handleDisplayForAdd() {
    setSelected({})
  }

  function handleDisplayForEdit(webCompany) {
    setSelected(webCompany)
  }

  function handleClose() {
    setSelected(null)
  }

  const handleAdminAccess = (webCompany) => async () => {
    const confirm = await confirmationDialog.open(
      'Personificar Empresa',
      `Deseja acessar o portal como ${webCompany.company.wee_razao_social} ?`
    )

    if (confirm) {
      dispatch(logIsAsCompanyRequest(webCompany))
    }

    confirmationDialog.close()
  }

  const handleChangeScope = (scopeSetter) => (e) => {
    const newScopeValue = e.target.value
    scopeSetter(newScopeValue)
    setSelectedFilters([])
    reset()
  }

  const handleSendInvite = (webCompany) => () =>
    dispatch(sendInviteRequest(webCompany))

  // ... renders

  function renderEmail({ cellValue, rowData: webCompany }) {
    return (
      <Box display="flex" alignItems="center">
        <span>{cellValue}</span>
        <Tooltip title={`Acessar como ${cellValue}`}>
          <LoginButton
            component={Signout}
            paddingLeft={1}
            onClick={handleAdminAccess(webCompany)}
          />
        </Tooltip>
      </Box>
    )
  }

  function renderSendInvite({ rowData: webCompany }) {
    const webUser = webCompany.admin_web_user

    if (!webUser) return null

    const title =
      webUser.invite_status === inviteStatusType.NOT_INVITED
        ? 'Enviar Convite'
        : 'Reenviar Convite'

    const isAccepted = webUser.invite_status === inviteStatusType.ACCEPTED

    return (
      <IconButton
        disabled={isAccepted}
        size="medium"
        onClick={handleSendInvite(webCompany)}
      >
        <Mail
          className={webUser.invite_status}
          fontSize="small"
          title={title}
        />
      </IconButton>
    )
  }

  function renderEdit({ rowData: webCompany }) {
    return (
      <IconButton
        size="medium"
        onClick={() => handleDisplayForEdit(webCompany)}
      >
        <Edit />
      </IconButton>
    )
  }

  useEffect(() => {
    fetchCompanies()
  }, [fetchCompanies])

  useEffect(() => {
    handleClose()
  }, [webCompanies])

  return (
    <Content
      title="Empresas"
      loading={loading}
      SideComponent={
        <Filter
          data={webCompanies}
          // loading={loading}
          selectedFilters={selectedFilters}
          onFiltering={setSelectedFilters}
        />
      }
      Controls={
        <>
          <ComboboxCompanyTypes
            value={companyType}
            onChange={handleChangeScope(setCompanyType)}
          />
          <ComboboxUserStatus
            value={status}
            onChange={handleChangeScope(setStatus)}
          />
          <ComboboxInviteStatus
            value={inviteStatus}
            onChange={handleChangeScope(setinviteStatus)}
          />

          <StyledButton
            color="primary"
            label="Adicionar"
            variant="contained"
            onClick={handleDisplayForAdd}
            endIcon={<Add />}
          />
        </>
      }
    >
      <Paper className="border-none">
        <Table
          size="medium"
          currentSort={currentSort}
          onSortChange={onSortChange}
          data={webCompanies}
          columns={[
            {
              title: 'TIPO',
              field: 'wee_tipo',
              renderCell: CompanyTypeChip,
            },
            { title: 'EMPRESA', field: 'wee_razao_social' },
            {
              title: 'CÓDIGO',
              field: 'cod_empresa',
            },
            {
              title: 'EMAIL',
              field: 'wee_email',
              renderCell: renderEmail,
            },
            {
              title: 'CNPJ',
              field: 'dsc_empresa',
              cellFormat: 'document',
            },
            {
              title: 'TELEFONE',
              field: 'wee_telefone',
              renderCell: ({ cellValue }) => (
                <>{formatter(unmask(cellValue || '')).toTelefone()}</>
              ),
            },

            { role: 'button', renderCell: renderSendInvite },
            { role: 'button', renderCell: renderEdit },
            {
              title: 'STATUS',
              field: 'wee_ativo',
              role: 'status',
              renderCell: CompanyStatus,
            },
          ]}
        />
        <TablePagination
          count={webCompaniesCount}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
        />
      </Paper>

      {selected && (
        <FormEdit
          open={Boolean(selected)}
          current={selected}
          onClose={handleClose}
        />
      )}

      <Box
        display="flex"
        flexDirection="column"
        gridGap="1rem"
        position="fixed"
        right="2rem"
        bottom="6rem"
      >
        <UpdateWebCompaniesButton onSuccess={fetchCompanies} />

        <BatchInvitationsButton onSuccess={fetchCompanies} />
      </Box>
    </Content>
  )
}

function CompanyTypeChip({ cellValue: type }) {
  // const companyTypes = getCompanyTypes()

  return (
    <Status
      label={companyTypes[type]?.title}
      className="secondary"
      size="small"
      mode="slim"
    />
  )
}

function CompanyStatus({ cellValue }) {
  return (
    <Status
      size="small"
      mode="slim"
      label={cellValue === 'active' ? 'Ativo' : 'Inativo'}
      className={cellValue === 'active' ? 'success' : 'error'}
    />
  )
}

export default AdminCompaniesPage
